import { PersonLabelContext, UserStatus } from '@bp/bp-graphql-types';
import { GroupRoles, ProfileRoles } from '@bp/pim-auth-constants';
import {
  AddIcon,
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Grid,
  GridColumn,
  GridRow,
  SendIcon,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import { BpHeader } from 'components/BpHeader/BpHeader';
import { GroupMembersModal } from 'components/Groups/modals/GroupMembersModal';
import { LoadingModal } from 'components/LoadingModal/LoadingModal';
import { PersonsLabelFilter } from 'components/Persons/components/PersonsLabelFilter/PersonsLabelFilter';
import { CreateUserModal } from 'components/Persons/modals/CreateUserModal';
import { PersonLabelSource, PersonLabelType, PersonListType } from 'components/Persons/types/personTypes';
import { accountHasPerson, labelIsInLabels } from 'components/Persons/utils/personUtils';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useConfirm } from 'hooks/useConfirm';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { ReactNode, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { getProfileRoleFromRoles } from 'utils/profileRoles';
import { naturalStringSort } from 'utils/stringHelper';
import { showSuccessToast, showUserDbErrorToast } from 'utils/toasts';
import { BpCard } from '../../../components/BpCard/BpCard';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { SimplePersonsList } from '../../../components/Persons/components/PersonsList/SimplePersonsList';
import { usePersonBulkActions } from '../../../components/Persons/hooks/usePersonBulkActions';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { redirectToDmMessagePage } from '../../../matrix/utils/redirectToDmMessagePage';
import styles from './GroupPersonsSubpage.module.scss';

const UpdateGroupsMutationDocument = graphql(`
  mutation BpUpdateGroupsInGroupSubpage($update: BpGroupUpdateInput!, $uuid: ID!) {
    updateBpGroup(update: $update, uuid: $uuid) {
      __typename
      uuid
      viewers {
        __typename
        uuid
      }
      editors {
        __typename
        uuid
      }
    }
  }
`);

const GroupPersonsSubpagePageQuery = graphql(`
  query GroupPersonsSubpagePageData(
    $groupUuid: ID!
    $labelUuids: [ID!]!
    $schoolyearUuid: ID!
    $labelContext: PersonLabelContext!
    $organizationUuid: ID!
  ) {
    threemaLicenseCount: threemas(where: { organization: { uuid: $organizationUuid } }) {
      __typename
      licenseCount
    }
    usedThreemaLicenses: profiles(
      where: {
        threemaConnection_SOME: { edge: { enabled: true }, node: { organization: { uuid: $organizationUuid } } }
      }
    ) {
      __typename
      uuid
    }
    groups(where: { uuid: $groupUuid, type: group }) {
      __typename
      uuid
      isCommunity
      managedBy
      admins {
        __typename
        uuid
        displayName
        organization {
          __typename
          uuid
        }
      }
      editors {
        __typename
        uuid
        updatedAt
        firstName
        lastName
        displayName
        listName
        email
        birthday
        user {
          __typename
          uuid
          status
        }
        organizationRoles
        organization {
          __typename
          uuid
        }
      }
      viewers {
        __typename
        uuid
        updatedAt
        firstName
        lastName
        displayName
        listName
        email
        birthday
        user {
          __typename
          uuid
          status
        }
        organizationRoles
        organization {
          __typename
          uuid
        }
      }
    }
    ...PersonsLabelFilterFields
    ...PersonsListFields
  }
`);

const CollaborationOrganizationsQuery = graphql(`
  query CollaborationOrganizationsData($organizationUuid: ID!) {
    collaboratingOrganizations(uuid: $organizationUuid) {
      __typename
      uuid
      name
    }
  }
`);

type GroupPersonListType = PersonListType & {
  organizationUuid: string;
  displayName: string | null;
  groupRole: GroupRoles;
  profileRole: ProfileRoles;
};

export const GroupPersonsSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const myOrganizationUuid = pimAuthClaims.getOrganizationUuid();
  const { threemaLicense, cloudLicense, currentSchoolYear } = useContext(OrganizationConfigContext);

  const { ConfirmationDialog, confirm: confirmRemove } = useConfirm({
    defaultTitle: t('groups.removeMember'),
    defaultConfirmText: t('common.remove'),
  });

  const { groupUuid } = useParams<{ groupUuid: string }>();
  const navigate = useNavigate();

  const [, bpUpdateGroup] = useMutation(UpdateGroupsMutationDocument);

  const [activeSources, setActiveSources] = useState<PersonLabelSource[]>(['ProfileRole']);
  const [filterMode, setFilterMode] = useState<Record<PersonLabelSource, 'and' | 'or'>>({
    AccessRole: 'and',
    AddOn: 'and',
    Child: 'and',
    Class: 'and',
    ClassGroup: 'and',
    GroupRole: 'and',
    Parent: 'and',
    ProfileRole: 'and',
    UserStatus: 'and',
  });
  const [search, setSearch] = useState<string>('');
  const [selectedLabels, setSelectedLabels] = useState<PersonLabelType[]>([]);

  const [personData, setPersonData] = useState<{
    uuid?: string;
    status?: string;
    roles?: string[];
    addContext?: GroupRoles;
  } | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
  const [membersModalOpen, setMembersModalOpen] = useState<boolean>(false);
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>();

  const [{ data }, refetchGroupData] = useQuery({
    query: GroupPersonsSubpagePageQuery,
    variables: {
      groupUuid: groupUuid ?? '',
      organizationUuid: myOrganizationUuid,
      labelUuids: groupUuid ? [groupUuid] : [],
      schoolyearUuid: currentSchoolYear.uuid,
      labelContext: PersonLabelContext.Group,
    },
  });

  const [{ data: collaborations }] = useQuery({
    query: CollaborationOrganizationsQuery,
    variables: { organizationUuid: myOrganizationUuid },
    pause: !data?.groups[0].isCommunity,
  });

  const group = data?.groups[0];

  const organizations = [
    {
      uuid: myOrganizationUuid,
      name: t('institution.myInstitution'),
    },
    ...(collaborations?.collaboratingOrganizations.filter((o) => {
      const hasEditors = group?.editors.some((e) => e.organization.uuid === o.uuid);
      const hasViewers = group?.viewers.some((v) => v.organization.uuid === o.uuid);
      const hasAdmins = group?.admins.some((a) => a.organization.uuid === o.uuid);
      return hasEditors || hasViewers || hasAdmins;
    }) ?? []),
  ];

  const permissions = usePermissionChecker();

  const isBpGroup = group?.managedBy === 'bp';

  const admins = group?.admins ?? [];

  const editors: GroupPersonListType[] = useMemo(
    () =>
      group?.editors
        .sort((a, b) => naturalStringSort(a.listName ?? '', b.listName ?? ''))
        .map((e) => ({
          uuid: e.uuid,
          organizationUuid: e.organization.uuid,
          firstName: e.firstName ?? null,
          lastName: e.lastName ?? null,
          displayName: e.displayName ?? null,
          listName: e.listName ?? null,
          email: e.email ?? null,
          birthday: e.birthday ?? null,
          userStatus: e.user?.status ?? UserStatus.None,
          updatedAt: e.updatedAt,
          groupRole: GroupRoles.Editor,
          profileRole: getProfileRoleFromRoles(e.organizationRoles),
        })) ?? [],
    [group?.editors],
  );

  const viewers: GroupPersonListType[] = useMemo(
    () =>
      group?.viewers
        .sort((a, b) => naturalStringSort(a.listName ?? '', b.listName ?? ''))
        .map((v) => ({
          uuid: v.uuid,
          organizationUuid: v.organization.uuid,
          firstName: v.firstName ?? null,
          lastName: v.lastName ?? null,
          displayName: v.displayName ?? null,
          listName: v.listName ?? null,
          email: v.email ?? null,
          birthday: v.birthday ?? null,
          userStatus: v.user?.status ?? UserStatus.None,
          updatedAt: v.updatedAt,
          groupRole: GroupRoles.Viewer,
          profileRole: getProfileRoleFromRoles(v.organizationRoles),
        })) ?? [],
    [group?.viewers],
  );

  const theemaLicense = data?.threemaLicenseCount[0];
  const theemaLicenseCount = theemaLicense?.licenseCount ?? 0;
  const usedThreemaLicenses = data?.usedThreemaLicenses?.length ?? 0;
  const availableThreemaLicenses = theemaLicenseCount - usedThreemaLicenses;

  // Bulk actions for editors
  const {
    selectedPersonUuids: selectedEditorUuids,
    handlePersonSelect: handleEditorSelect,
    handleSelectAll: handleSelectAllEditors,
    clearSelection: clearEditorSelection,
    handleAddThreema: handleAddThreemaEditors,
    handleBulkNextcloud: handleBulkNextcloudEditors,
    ConfirmationDialogs: EditorConfirmationDialogs,
  } = usePersonBulkActions({
    freeThreemaLicenseCount: availableThreemaLicenses,
    onSuccess: () => refetchGroupData({ requestPolicy: 'network-only' }),
  });

  // Bulk actions for viewers
  const {
    selectedPersonUuids: selectedViewerUuids,
    handlePersonSelect: handleViewerSelect,
    handleSelectAll: handleSelectAllViewers,
    clearSelection: clearViewerSelection,
    handleAddThreema: handleAddThreemaViewers,
    handleBulkNextcloud: handleBulkNextcloudViewers,
    ConfirmationDialogs: ViewerConfirmationDialogs,
  } = usePersonBulkActions({
    freeThreemaLicenseCount: availableThreemaLicenses,
    onSuccess: () => refetchGroupData({ requestPolicy: 'network-only' }),
  });

  const emptySelection =
    (threemaLicense || cloudLicense) && selectedEditorUuids.length + selectedViewerUuids.length === 0;

  const canEditCloudPermissions =
    permissions.canManageThreema(pimAuthClaims.getOrganization()) ||
    permissions.canManageNextcloud(pimAuthClaims.getOrganization());

  const canEditGroupMembers = groupUuid
    ? permissions.canChangeBpCoursePermissions({
        uuid: groupUuid,
        organization: { uuid: myOrganizationUuid },
      })
    : false;

  const AddButton: ReactNode = (
    <Dropdown
      triggerClass={styles.button}
      disabled={!isBpGroup}
      trigger={
        <Button disabled={!isBpGroup} icon={<AddIcon />} hierarchy='tertiary' onClick={() => handleMembersEdit()}>
          {t('groups.addMembers')}
        </Button>
      }
    >
      <DropdownMenu
        data={[
          {
            label: t('common.editor'),
            onClick: () => handleAddMember(GroupRoles.Editor),
          },
          {
            label: t('common.viewer'),
            onClick: () => handleAddMember(GroupRoles.Viewer),
          },
        ]}
      />
    </Dropdown>
  );

  function getActions(personUuid: string): ReactNode {
    const allPersons = [...editors, ...viewers];
    const person = allPersons.find((p) => p.uuid === personUuid);
    if (!person) return [];

    const items: DropdownMenuItem[] = [];

    const { uuid, organizationUuid, groupRole, profileRole, userStatus, firstName, lastName } = person;

    const canMessage = permissions.canDirectMessageWithinGroup(
      {
        uuid: groupUuid ?? '',
        organization: { uuid: organizationUuid },
      },
      {
        uuid: uuid,
        organization: { uuid: organizationUuid },
        role: profileRole,
      },
    );

    if (canEditGroupMembers) {
      items.push({
        label: t('groups.roleInGroup'),
        subContent: [
          {
            label: t('common.editor'),
            onClick: () => {
              handleGroupRoleChange(uuid, 'editor');
            },
            disabled: groupRole === GroupRoles.Editor,
          },
          {
            label: t('common.viewer'),
            onClick: () => {
              handleGroupRoleChange(uuid, 'viewer');
            },
            disabled: groupRole === GroupRoles.Viewer,
          },
        ],
      });

      if (userStatus === UserStatus.None) {
        items.push({
          label: t('profiles.invite.sendInvitation'),
          disabled: userStatus !== UserStatus.None,
          onClick: () => {
            handleInvite(uuid, userStatus);
          },
        });
      } else if (userStatus === UserStatus.Invited) {
        items.push({
          label: t('profiles.invite.resendInvitation'),
          disabled: userStatus !== UserStatus.Invited,
          onClick: () => {
            handleInvite(uuid, userStatus);
          },
        });
      }

      items.push({ type: 'ruler' });

      items.push({
        label: t('groups.removeMember'),
        color: 'error',
        onClick: () => {
          handleRemoveMember(uuid, `${firstName} ${lastName}`);
        },
        disabled: !isBpGroup,
      });
    }

    return (
      <>
        <Button
          hierarchy='ghost'
          icon={<SendIcon />}
          onClick={() => handleChat(uuid, profileRole)}
          disabled={!accountHasPerson(userStatus) || !canMessage}
        />
        {organizationUuid === myOrganizationUuid && items.length > 0 && (
          <Dropdown trigger={<Button hierarchy='ghost' icon={<DotsVerticalIcon />} />}>
            <DropdownMenu data={items} />
          </Dropdown>
        )}
      </>
    );
  }

  async function handleGroupRoleChange(uuid: string, target: 'editor' | 'viewer') {
    if (!groupUuid) return;

    setLoadingModalOpen(true);
    setLoadingMessage(t('groups.changeGroupRoleInProgess'));

    let editorUuids: string[] = editors.map((e) => e.uuid) ?? [];
    let viewerUuids: string[] = viewers.map((v) => v.uuid) ?? [];

    if (target === 'editor') {
      editorUuids.push(uuid);
      viewerUuids = viewerUuids.filter((v) => v !== uuid);
    } else {
      editorUuids = editorUuids.filter((e) => e !== uuid);
      viewerUuids.push(uuid);
    }

    const { error } = await bpUpdateGroup({
      uuid: groupUuid,
      update: { editors: editorUuids, viewers: viewerUuids },
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessToast();
    }

    reset();
  }

  function handleInvite(uuid: string, userStatus: string) {
    setPersonData({ uuid: uuid, status: userStatus });
    setInviteModalOpen(true);
  }

  async function handleChat(uuid: string, role: ProfileRoles) {
    if (!groupUuid) return;

    setLoadingModalOpen(true);

    await redirectToDmMessagePage({
      groupUuid: groupUuid,
      targetProfileUuid: uuid,
      targetProfileOrganizationUuid: '',
      targetProfileRole: role,
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      context: 'group',
      permissionChecker: permissions,
      navigate,
    });

    reset();
  }

  function handleAddMember(role: GroupRoles) {
    setPersonData({ addContext: role });
    setMembersModalOpen(true);
  }

  async function handleRemoveMember(uuid: string, name: string) {
    if (!groupUuid) return;

    const confirm = await confirmRemove({
      message: t('groups.removeMemberMessage', { name }),
    });
    if (!confirm) return;

    setLoadingModalOpen(true);
    setLoadingMessage(t('groups.removeMemberInProgress'));

    const editorUuids = editors.filter((e) => e.uuid !== uuid).map((e) => e.uuid);
    const viewerUuids = viewers.filter((v) => v.uuid !== uuid).map((v) => v.uuid);

    const { error } = await bpUpdateGroup({
      uuid: groupUuid,
      update: { editors: editorUuids, viewers: viewerUuids },
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessToast();
    }

    reset();
  }

  function handleLabelAdd(label: PersonLabelType) {
    setSelectedLabels((prev) => {
      if (!labelIsInLabels(label, prev)) {
        return [...prev, label];
      }
      return [...prev];
    });
  }

  function handleLabelClose(label: PersonLabelType) {
    setSelectedLabels((prev) => {
      if (labelIsInLabels(label, prev)) {
        return prev.filter((l) => l !== label);
      }
      return [...prev];
    });
  }

  function handleCloseAllLabels() {
    setSelectedLabels([]);
  }

  function handleSearch(value: string) {
    setSearch(value);
  }

  function handleSourceFilter(sources: PersonLabelSource[]) {
    setActiveSources(sources);
  }

  function handleFilterModeChange(source: PersonLabelSource, mode: 'and' | 'or') {
    setFilterMode((prev) => ({
      ...prev,
      [source]: mode,
    }));
  }

  function handleMembersEdit() {
    setMembersModalOpen(true);
  }

  function reset() {
    setPersonData(null);
    setInviteModalOpen(false);
    setMembersModalOpen(false);
    setLoadingModalOpen(false);
    setLoadingMessage(null);
    clearEditorSelection();
    clearViewerSelection();
    refetchGroupData({ requestPolicy: 'network-only' });
  }

  async function handleBulkNextcloud() {
    await Promise.all([
      selectedEditorUuids.length > 0 ? handleBulkNextcloudEditors(selectedEditorUuids) : Promise.resolve(),
      selectedViewerUuids.length > 0 ? handleBulkNextcloudViewers(selectedViewerUuids) : Promise.resolve(),
    ]);
  }

  async function handleAddThreema() {
    await Promise.all([
      selectedEditorUuids.length > 0 ? handleAddThreemaEditors(selectedEditorUuids) : Promise.resolve(),
      selectedViewerUuids.length > 0 ? handleAddThreemaViewers(selectedViewerUuids) : Promise.resolve(),
    ]);
  }

  const bulkEditContent: DropdownMenuItem[] = [
    ...(cloudLicense
      ? [
          {
            label: t('externalServices.createCloudAccount'),
            onClick: () => handleBulkNextcloud(),
            disabled: emptySelection,
          },
        ]
      : []),
    ...(threemaLicense
      ? [
          {
            label: t('externalServices.assignThreemaLicense'),
            onClick: () => handleAddThreema(),
            disabled: emptySelection,
          },
        ]
      : []),
  ];

  return (
    <BpSubpage className={styles['group-persons-subpage']}>
      <Grid>
        <GridRow spacingBottom='l'>
          <PersonsLabelFilter
            selectedLabels={selectedLabels}
            showSourcesFilter
            availableSources={['AddOn', 'Class', 'ClassGroup', 'ProfileRole', 'UserStatus']}
            activeSources={activeSources}
            filterMode={filterMode}
            showSearch
            showBulkEdit={canEditCloudPermissions}
            bulkEditDisabled={emptySelection}
            bulkEditContent={bulkEditContent}
            showAddButton={canEditGroupMembers}
            addButtonContent={AddButton}
            onLabelClose={(label) => handleLabelClose(label)}
            onCloseAll={() => handleCloseAllLabels()}
            onSearch={(value) => handleSearch(value)}
            onSourceFilter={(sources) => handleSourceFilter(sources)}
            onFilterModeChange={handleFilterModeChange}
            data={data}
          />
        </GridRow>

        {organizations?.map((organization) => {
          const uuid = organization.uuid;

          const organizationAdmins = admins.filter((a) => a.organization.uuid === uuid);
          const organizationViewers = viewers.filter((v) => v.organizationUuid === uuid);
          const organizationEditors = editors.filter((e) => e.organizationUuid === uuid);

          return (
            <div key={uuid} className={styles.organization}>
              <BpHeader
                size={organizations.length > 1 ? 'm' : 's'}
                headline={
                  organizations.length > 1
                    ? organization.uuid === myOrganizationUuid
                      ? t('institution.myInstitution')
                      : (organization?.name ?? '')
                    : t('institution.admin')
                }
                subHeadline={
                  organizations.length > 1
                    ? `${t('institution.admin')}: ${organizationAdmins.map((a) => a.displayName).join(', ')}`
                    : organizationAdmins.map((a) => a.displayName).join(', ')
                }
                noMargin
              />
              <GridRow mobileGap='var(--spacing-5)'>
                <GridColumn width={6}>
                  <BpCard
                    header={{
                      headline: t('common.editor'),
                      subHeadline: organizationEditors.length,
                    }}
                    noPadding
                  >
                    <SimplePersonsList
                      persons={organizationEditors}
                      selectedLabels={selectedLabels}
                      search={search}
                      activeSources={activeSources}
                      onGetActions={(uuid) => getActions(uuid)}
                      padding='m'
                      onLabelClick={(label) => handleLabelAdd(label)}
                      data={data}
                      onSelectCheckbox={(uuid) => handleEditorSelect(uuid)}
                      selectedPersons={selectedEditorUuids}
                      onSelectAll={handleSelectAllEditors}
                    />
                  </BpCard>
                </GridColumn>
                <GridColumn width={6}>
                  <BpCard
                    header={{
                      headline: t('common.viewer'),
                      subHeadline: organizationViewers.length,
                    }}
                    noPadding
                  >
                    <SimplePersonsList
                      persons={organizationViewers}
                      selectedLabels={selectedLabels}
                      search={search}
                      activeSources={activeSources}
                      onGetActions={(uuid) => getActions(uuid)}
                      padding='m'
                      onLabelClick={(label) => handleLabelAdd(label)}
                      data={data}
                      onSelectCheckbox={(uuid) => handleViewerSelect(uuid)}
                      selectedPersons={selectedViewerUuids}
                      onSelectAll={handleSelectAllViewers}
                    />
                  </BpCard>
                </GridColumn>
              </GridRow>
            </div>
          );
        })}
      </Grid>

      {groupUuid && personData?.addContext && (
        <GroupMembersModal
          groupUuid={groupUuid}
          role={personData.addContext}
          isOpen={membersModalOpen}
          onClose={() => reset()}
        />
      )}

      {personData && personData.uuid && personData.status && (
        <CreateUserModal
          isOpen={inviteModalOpen}
          profileUuid={personData.uuid}
          resend={personData.status === UserStatus.Invited}
          onClose={() => reset()}
        />
      )}

      <LoadingModal
        isOpen={loadingModalOpen}
        message={loadingMessage ?? undefined}
        onClose={() => {
          setLoadingModalOpen(false);
          setLoadingMessage(null);
        }}
      />

      <ConfirmationDialog />
      <EditorConfirmationDialogs />
      <ViewerConfirmationDialogs />
    </BpSubpage>
  );
};
